import * as yup from 'yup';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";

/**
 * 🚀 GLOBAL SCHEMA HELPER
 * 
 * Utilitários genéricos para trabalhar com schemas Yup contextuais.
 * Pode ser usado por qualquer fieldset que implemente a arquitetura
 * de schema unificado com metadados contextuais.
 */

// Interface genérica para metadados contextuais em schemas Yup
export interface ContextualFieldMetadata {
    contexts: CollaboratorFormContext[];
    conditional?: (formData: any) => boolean;
}

/**
 * Extrai valores padrão de um schema Yup para garantir que não hajam campos undefined
 * @param schema Schema Yup
 * @returns Objeto com valores padrão
 */
export const getSchemaDefaults = (schema: any): Record<string, any> => {
    const defaults: Record<string, any> = {};

    if (schema && schema.fields) {
        Object.keys(schema.fields).forEach(key => {
            const field = schema.fields[key];

            if (field && typeof field.getDefault === 'function') {
                try {
                    const defaultValue = field.getDefault();
                    if (defaultValue !== undefined) {
                        defaults[key] = defaultValue;
                    } else {
                        // Garantir valores padrão baseados no tipo
                        if (field.type === 'string') {
                            defaults[key] = '';
                        } else if (field.type === 'number') {
                            defaults[key] = 0;
                        } else if (field.type === 'boolean') {
                            defaults[key] = false;
                        } else if (field.type === 'array') {
                            defaults[key] = [];
                        } else if (field.type === 'object') {
                            defaults[key] = {};
                        } else {
                            defaults[key] = null;
                        }
                    }
                } catch (error) {
                    // Se não conseguir obter o padrão, usar valores seguros baseados no tipo
                    if (field.type === 'string') {
                        defaults[key] = '';
                    } else if (field.type === 'number') {
                        defaults[key] = 0;
                    } else if (field.type === 'boolean') {
                        defaults[key] = false;
                    } else {
                        defaults[key] = null;
                    }
                }
            }
        });
    }

    return defaults;
};

/**
 * Extrai configuração contextual de qualquer schema Yup
 * @param schema - Schema Yup com metadados contextuais
 * @param context - Contexto atual do formulário
 * @returns Configuração dos campos para o contexto
 */
export const getContextualConfig = (schema: yup.ObjectSchema<any>, context: CollaboratorFormContext) => {
    const fields = schema.fields;
    const contextualFields: Record<string, boolean> = {};
    const requiredFields: string[] = [];

    Object.entries(fields).forEach(([fieldName, fieldSchema]) => {
        // Type guard para verificar se é um schema Yup com spec
        if (fieldSchema && typeof fieldSchema === 'object' && 'spec' in fieldSchema) {
            const metadata = (fieldSchema as any).spec?.meta as ContextualFieldMetadata;

            if (metadata?.contexts.includes(context)) {
                contextualFields[fieldName] = true;

                // Verificar se o campo é obrigatório neste contexto
                if ('validateSync' in fieldSchema) {
                    try {
                        (fieldSchema as any).validateSync('', { context: { context } });
                    } catch (error: any) {
                        if (error.message?.includes('obrigatório') || error.message?.includes('required')) {
                            requiredFields.push(fieldName);
                        }
                    }
                }
            }
        }
    });

    return {
        fields: contextualFields,
        requiredFields
    };
};

/**
 * Verifica se um campo deve aparecer no contexto atual
 * @param schema - Schema Yup com metadados contextuais
 * @param fieldName - Nome do campo
 * @param context - Contexto atual do formulário
 * @param formData - Dados atuais do formulário (para condicionais)
 * @returns true se o campo deve aparecer
 */
export const shouldShowField = (
    schema: yup.ObjectSchema<any>,
    fieldName: string,
    context: CollaboratorFormContext,
    formData?: any
): boolean => {
    const fieldSchema = schema.fields[fieldName];

    // Type guard para verificar se é um schema Yup com spec
    if (fieldSchema && typeof fieldSchema === 'object' && 'spec' in fieldSchema) {
        const metadata = (fieldSchema as any).spec?.meta as ContextualFieldMetadata;

        if (!metadata?.contexts.includes(context)) {
            return false;
        }

        if (metadata.conditional && formData) {
            return metadata.conditional(formData);
        }

        return true;
    }

    return false;
};

/**
 * Helper genérico para extrair mensagem de erro do React Hook Form
 * @param errors - Objeto de erros do React Hook Form
 * @param fieldPath - Path do campo
 * @returns Mensagem de erro ou undefined
 */
export const getErrorMessage = (errors: any, fieldPath: string): string | undefined => {
    if (!errors) return undefined;

    // Suporta paths aninhados: addresses.0.number
    const segments = fieldPath.split('.');
    let current: any = errors;

    for (const seg of segments) {
        if (current && typeof current === 'object') {
            current = current[seg];
        } else {
            current = undefined;
            break;
        }
    }

    if (!current) return undefined;

    if (typeof current === 'string') return current;
    if (typeof current === 'object' && 'message' in current) {
        return (current as any).message;
    }
    return 'Erro de validação';
};

/**
 * Helper genérico para verificar se um campo é obrigatório no contexto atual
 * @param schema - Schema Yup com metadados contextuais
 * @param fieldName - Nome do campo
 * @param context - Contexto atual do formulário
 * @returns true se o campo é obrigatório
 */
export const isFieldRequired = (
    schema: yup.ObjectSchema<any>,
    fieldName: string,
    context: CollaboratorFormContext
): boolean => {
    const config = getContextualConfig(schema, context);
    return config.requiredFields.includes(fieldName);
};

/**
 * Cria um wrapper para facilitar o uso dos helpers com um schema específico
 * @param schema - Schema Yup específico
 * @returns Objeto com helpers pré-configurados para o schema
 */
export const createSchemaHelpers = (schema: yup.ObjectSchema<any>) => ({
    getDefaults: () => getSchemaDefaults(schema),
    getConfig: (context: CollaboratorFormContext) => getContextualConfig(schema, context),
    shouldShow: (fieldName: string, context: CollaboratorFormContext, formData?: any) =>
        shouldShowField(schema, fieldName, context, formData),
    getErrorMessage: (errors: any, fieldPath: string) => getErrorMessage(errors, fieldPath),
    isRequired: (fieldName: string, context: CollaboratorFormContext) =>
        isFieldRequired(schema, fieldName, context)
}); 